/**
 * AppDiscardModal — tira a app do catálogo. ZERO chamada à AWS.
 *
 * É o verbo da app que nasceu FORA do pipeline (`ztna_provision_jobs` vazio).
 * Para ela o `deprovision` só pode devolver 422 `no_provision_history`: sem
 * snapshot de provision não há DNS, regra de balanceador nem certificado
 * **nossos** para reverter.
 *
 * **Esta modal não lê nada antes de abrir**, e isso é o desenho, não descuido.
 * Até 31/08/2026 o descarte só existia dentro do modal de adoção, condicionado
 * ao sucesso do `adoption-preview` — que chama `AssumeRole` na conta do tenant.
 * Nos tenants 3 (`tenant_account_id_missing`) e 93 (`tenant_assume_denied`) o
 * preview falha, e o verbo cujo contrato é "zero chamada à AWS" ficava
 * indisponível exatamente quando a AWS do tenant não responde. Três das cinco
 * apps da classe não tinham saída nenhuma na tela.
 *
 * Diz item a item o que NÃO faz: modal que diz só "descartar" mente por
 * omissão — a app some da lista e o hostname continua respondendo.
 */
import { useState } from 'react'
import {
  Alert,
  Button,
  Group,
  List,
  Modal,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconArchive, IconInfoCircle } from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ztnaApi } from '../api'
import type { AppHealthSummary } from '../types'
import { formatApiError } from '@/api/errors'

interface Props {
  app: AppHealthSummary
  opened: boolean
  onClose: () => void
}

export function AppDiscardModal({ app, opened, onClose }: Props) {
  const queryClient = useQueryClient()

  const [confirmText, setConfirmText] = useState('')

  // Exato, não prefixo: o backend recusa com `hostname_mismatch`, e a tela não
  // deve deixar o operador descobrir isso pelo erro.
  const canSubmit = confirmText === app.hostname

  const descartar = useMutation({
    // O digitado é confirmação de intenção; o que vai ao backend é o valor
    // canônico da row.
    mutationFn: () => ztnaApi.discardApp(app.id, app.hostname),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['ztna', 'apps'] })
      queryClient.invalidateQueries({
        queryKey: ['ztna', 'app-audit-events', app.id],
      })
      notifications.show({
        // `orange`, não `red`: vermelho é o destrutivo que apaga
        // infraestrutura, e aqui nada em AWS é apagado.
        color: 'orange',
        title: 'Aplicação descartada do catálogo',
        message:
          `Ela sai da lista. Continuam de pé: ${r.nao_tocado.join(', ')} — nada em AWS foi tocado.`,
      })
      onClose()
    },
    onError: (err: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Não foi possível descartar',
        message: formatApiError(err, 'Não foi possível descartar a aplicação.'),
      })
    },
  })

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Descartar ${app.slug}`}
      size="lg"
    >
      <Stack gap="md">
        <Alert
          color="orange"
          variant="light"
          icon={<IconInfoCircle size={16} />}
          title="Sai do catálogo — nada em AWS é tocado"
        >
          <Stack gap={6}>
            <Text size="sm">
              Esta aplicação não tem histórico de provisionamento: ela foi criada fora do
              pipeline, então não existe nada provisionado por nós para reverter. O que
              continua de pé:
            </Text>
            <List size="sm" spacing={2}>
              <List.Item>o registro DNS continua onde está</List.Item>
              <List.Item>o certificado continua emitido</List.Item>
              <List.Item>a regra do balanceador continua lá</List.Item>
            </List>
            <Text size="sm">
              O hostname pode continuar respondendo depois do descarte. Se a intenção é
              tirá-lo do ar, isso é trabalho na conta do tenant.
            </Text>
          </Stack>
        </Alert>

        <Stack gap={6}>
          <Text size="sm">Para confirmar, digite o hostname exato:</Text>
          {/* NAO `dimmed`: DESIGN_SYSTEM §11.2 manda `var(--ai-text-secondary)`
              (4,8:1) quando a informacao e' importante, e este e' o valor que o
              operador tem de copiar para o campo abaixo. Em `dimmed` sobre
              branco ele ficava indistinguivel do placeholder do proprio input.
              0.8rem e' o tamanho documentado para dado em JetBrains Mono. */}
          <Text
            fz="0.8rem"
            fw={500}
            ff="JetBrains Mono, monospace"
            c="var(--ai-text-secondary)"
          >
            {app.hostname}
          </Text>
          {/* `ff` no TextInput do Mantine cai no WRAPPER, nao no <input>: medido no
              navegador em 31/08/2026, o computed do input era `Barlow, -apple-system, …`.
              O campo existe para comparar o hostname caractere por caractere com a
              referencia acima, que esta em JetBrains Mono — em duas tipografias
              diferentes a comparacao fica mais dificil justamente onde ela importa.
              `styles.input` acerta o elemento certo. */}
          <TextInput
            value={confirmText}
            onChange={(e) => setConfirmText(e.currentTarget.value)}
            placeholder={app.hostname}
            styles={{ input: { fontFamily: '"JetBrains Mono", monospace' } }}
          />
        </Stack>

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            color="orange"
            leftSection={<IconArchive size={14} />}
            onClick={() => descartar.mutate()}
            loading={descartar.isPending}
            disabled={!canSubmit}
          >
            Descartar do catálogo
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

export default AppDiscardModal
